// controllers/performanceController.js
import apiService from '../services/apiService';
import Performance from '../models/Performance';

export const performanceController = {
  /**
   * Get all performance reviews with filters
   * @param {Object} filters - { page, limit, status, employeeId, reviewerId }
   * @returns {Promise<Object>} - { success, data, total, pagination }
   */
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `performance?${queryParams}` : 'performance';
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Performance.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length,
        pagination: response.pagination || null
      };
    } catch (error) {
      console.error('Error fetching performance reviews:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch performance reviews' 
      };
    }
  },

  /**
   * Get single performance review by ID
   * @param {string} id - Performance ID
   * @returns {Promise<Object>} - { success, data }
   */
  getById: async (id) => {
    try {
      const response = await apiService.get(`performance/${id}`);
      return { 
        success: true, 
        data: Performance.fromJSON(response.data) 
      };
    } catch (error) {
      console.error('Error fetching performance review:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Get performance reviews by employee ID
   * @param {string} employeeId - Employee ID
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - { success, data, total }
   */
  getByEmployeeId: async (employeeId, filters = {}) => {
    try {
      const queryParams = new URLSearchParams({ ...filters, employeeId }).toString();
      const endpoint = `performance?${queryParams}`;
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Performance.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length 
      };
    } catch (error) {
      console.error('Error fetching employee performance:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Get performance reviews by reviewer ID
   * @param {string} reviewerId - Reviewer ID
   * @param {Object} filters - Additional filters
   * @returns {Promise<Object>} - { success, data, total }
   */
  getByReviewerId: async (reviewerId, filters = {}) => {
    try {
      const queryParams = new URLSearchParams({ ...filters, reviewerId }).toString();
      const endpoint = `performance?${queryParams}`;
      const response = await apiService.get(endpoint);
      
      const items = response.data?.map(item => Performance.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: response.total || items.length 
      };
    } catch (error) {
      console.error('Error fetching reviewer performance:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Create new performance review
   * @param {Object} data - Performance review data
   * @returns {Promise<Object>} - { success, data, message }
   */
  create: async (data) => {
    try {
      const performance = new Performance(data);
      const validation = performance.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors,
          message: 'Validation failed'
        };
      }
      
      const response = await apiService.post('performance', performance.toJSON());
      
      return { 
        success: true, 
        data: Performance.fromJSON(response.data), 
        message: response.message || 'Performance review created successfully' 
      };
    } catch (error) {
      console.error('Error creating performance review:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to create performance review' 
      };
    }
  },

  /**
   * Update performance review
   * @param {string} id - Performance ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} - { success, data, message }
   */
  update: async (id, data) => {
    try {
      const performance = new Performance({ ...data, id });
      const validation = performance.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors 
        };
      }
      
      const response = await apiService.put(`performance/${id}`, performance.toJSON());
      
      return { 
        success: true, 
        data: Performance.fromJSON(response.data), 
        message: response.message || 'Performance review updated successfully' 
      };
    } catch (error) {
      console.error('Error updating performance review:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to update performance review' 
      };
    }
  },

  /**
   * Delete performance review
   * @param {string} id - Performance ID
   * @returns {Promise<Object>} - { success, message }
   */
  delete: async (id) => {
    try {
      const response = await apiService.delete(`performance/${id}`);
      
      return { 
        success: true, 
        message: response.message || 'Performance review deleted successfully' 
      };
    } catch (error) {
      console.error('Error deleting performance review:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to delete performance review' 
      };
    }
  },

  /**
   * Submit performance review (change status to Submitted)
   * @param {string} id - Performance ID
   * @returns {Promise<Object>} - { success, data, message }
   */
  submit: async (id) => {
    try {
      const response = await apiService.patch(`performance/${id}/submit`);
      
      return { 
        success: true, 
        data: Performance.fromJSON(response.data), 
        message: response.message || 'Performance review submitted successfully' 
      };
    } catch (error) {
      console.error('Error submitting performance review:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to submit performance review' 
      };
    }
  },

  /**
   * Complete performance review (change status to Completed)
   * @param {string} id - Performance ID
   * @returns {Promise<Object>} - { success, data, message }
   */
  complete: async (id) => {
    try {
      const response = await apiService.patch(`performance/${id}/complete`);
      
      return { 
        success: true, 
        data: Performance.fromJSON(response.data), 
        message: response.message || 'Performance review completed successfully' 
      };
    } catch (error) {
      console.error('Error completing performance review:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to complete performance review' 
      };
    }
  },

  /**
   * Get performance statistics
   * @param {Object} filters - { employeeId, reviewerId, startDate, endDate }
   * @returns {Promise<Object>} - { success, data }
   */
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams 
        ? `performance/statistics?${queryParams}` 
        : 'performance/statistics';
      
      const response = await apiService.get(endpoint);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Get average ratings
   * @param {string} employeeId - Employee ID (optional)
   * @returns {Promise<Object>} - { success, data }
   */
  getAverageRatings: async (employeeId = null) => {
    try {
      const endpoint = employeeId 
        ? `performance/average?employeeId=${employeeId}` 
        : 'performance/average';
      
      const response = await apiService.get(endpoint);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching average ratings:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Get performance trends
   * @param {string} employeeId - Employee ID
   * @param {number} months - Number of months
   * @returns {Promise<Object>} - { success, data }
   */
  getTrends: async (employeeId, months = 12) => {
    try {
      const response = await apiService.get(`performance/trends?employeeId=${employeeId}&months=${months}`);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching trends:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message 
      };
    }
  },

  /**
   * Export performance reviews
   * @param {Object} filters - Export filters
   * @param {string} format - 'csv' | 'excel' | 'pdf'
   * @returns {Promise<Object>} - { success, data }
   */
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`performance/export?${queryParams}`);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error exporting performance reviews:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to export performance reviews' 
      };
    }
  },

  /**
   * Get pending reviews
   * @returns {Promise<Object>} - { success, data, total }
   */
  getPendingReviews: async () => {
    try {
      const response = await apiService.get('performance?status=Draft');
      const items = response.data?.map(item => Performance.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: items, 
        total: items.length 
      };
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  },

  /**
   * Calculate overall rating from individual ratings
   * @param {Object} ratings - Individual ratings
   * @returns {number} - Overall rating
   */
  calculateOverallRating: (ratings) => {
    const { technicalSkills, communication, teamwork, leadership, punctuality } = ratings;
    const total = technicalSkills + communication + teamwork + leadership + punctuality;
    const count = 5;
    return parseFloat((total / count).toFixed(2));
  },

  /**
   * Get top performers
   * @param {number} limit - Number of top performers
   * @returns {Promise<Object>} - { success, data }
   */
  getTopPerformers: async (limit = 10) => {
    try {
      const response = await apiService.get(`performance/top?limit=${limit}`);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching top performers:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message 
      };
    }
  }
};

export default performanceController;